import { Controller } from "@hotwired/stimulus"

// A kit scale input (app/views/kit/_scale_input). The radio group and the
// stylesheet do the work: choosing, the filled run, the keyboard. This adds
// what a radio group cannot do on its own: a second tap on the chosen point
// clears it, Space/Backspace/Delete on the chosen point clears it from the
// keyboard, the named value beside the label follows along, and a form
// reset repaints the fieldset.
export default class extends Controller {
  static targets = ["radio", "value"]

  connect() {
    this.chosen = this.radioTargets.find((radio) => radio.checked) || null
    this.paint()

    this.onReset = () => setTimeout(() => this.paint(), 0)
    this.element.form?.addEventListener("reset", this.onReset)
  }

  disconnect() {
    this.element.form?.removeEventListener("reset", this.onReset)
  }

  // click, which a tap, a label click and the arrow keys all raise. Space on
  // the chosen radio raises no click in Chrome, which is what `clear` is for.
  toggle(event) {
    const radio = event.currentTarget
    if (radio === this.chosen) {
      this.uncheck(radio)
    } else {
      this.chosen = radio
    }
    this.paint()
  }

  // Space, Backspace or Delete on the already-chosen radio: no click follows,
  // so this clears it directly. Any other key, or an unchecked radio (Space
  // there should set it natively), does nothing.
  clear(event) {
    if (![" ", "Backspace", "Delete"].includes(event.key)) return
    const radio = event.currentTarget
    if (!radio.checked) return

    event.preventDefault()
    this.uncheck(radio)
    this.paint()
  }

  uncheck(radio) {
    radio.checked = false
    this.chosen = null
    radio.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // Resyncs `chosen` against the checked radio, so a checkedness change that
  // did not come through toggle or clear cannot leave it stale. Runs on
  // every change, on connect, and (a tick after) on a form reset.
  paint() {
    const radio = this.radioTargets.find((candidate) => candidate.checked)
    this.chosen = radio || null
    this.element.classList.toggle("is-set", Boolean(radio))
    if (this.hasValueTarget) {
      this.valueTarget.textContent = radio ? radio.dataset.label : this.valueTarget.dataset.unset
    }
  }
}
